/**
 * Input hardening.
 *
 * React escapes everything it renders, so the realistic front-end risks are:
 *  1. values we hand to non-React sinks (URLs, `window.location`, storage),
 *  2. malformed values reaching the API and confusing the server,
 *  3. oversized payloads used to wedge the UI or the backend.
 *
 * Everything below is defensive normalisation. It is NOT a substitute for
 * server-side validation — see SECURITY.md.
 */

/** Control characters, bidi overrides, and zero-width joiners used to spoof text. */
// eslint-disable-next-line no-control-regex
const DANGEROUS_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

export const LIMITS = {
  name: 80,
  unitNumber: 10,
  description: 300,
  title: 120,
  resolution: 300,
  search: 60,
  phone: 20,
  money: 12,
  publicId: 40,
} as const;

/**
 * Strips control/bidi characters, collapses whitespace, and enforces a hard
 * length cap. Every free-text field passes through here before leaving the UI.
 */
export function sanitizeText(input: unknown, maxLength: number = LIMITS.description): string {
  if (typeof input !== 'string') return '';
  return input
    .replace(DANGEROUS_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);
}

/** Multi-line variant: keeps newlines, drops everything else dangerous. */
export function sanitizeMultiline(input: unknown, maxLength: number = LIMITS.description): string {
  if (typeof input !== 'string') return '';
  return input
    .replace(DANGEROUS_CHARS, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, maxLength);
}

/** Unit numbers are digits and an optional single letter — nothing else. */
export function sanitizeUnitNumber(input: unknown): string {
  return sanitizeText(input, LIMITS.unitNumber).replace(/[^0-9A-Za-z\u0660-\u0669-]/g, '');
}

/** Search terms never carry syntax; strip anything that looks like an operator. */
export function sanitizeSearch(input: unknown): string {
  return sanitizeText(input, LIMITS.search).replace(/[<>"'`;\\{}$]/g, '');
}

/** Money is only ever digits with up to three decimals. */
export function sanitizeMoneyInput(input: unknown): string {
  if (typeof input !== 'string') return '';
  const cleaned = input.replace(/[^\d.]/g, '').slice(0, LIMITS.money);
  const [whole, ...rest] = cleaned.split('.');
  return rest.length === 0 ? whole : `${whole}.${rest.join('').slice(0, 3)}`;
}

export function sanitizePhoneInput(input: unknown): string {
  if (typeof input !== 'string') return '';
  return input.replace(/[^\d+\s-]/g, '').slice(0, LIMITS.phone);
}

export function sanitizeOtpInput(input: unknown): string {
  if (typeof input !== 'string') return '';
  return input.replace(/\D/g, '').slice(0, 6);
}

/* --------------------------------------------------- route param guards */

/*
  يجب أن يطابق `ck_public_id_shape` في القاعدة.

  كان يقبل شرطة واحدة فقط. ومولِّد المعرّف يبنيه من الاسم الإنجليزي بعد
  تحويل كل ما ليس حرفاً أو رقماً إلى شرطة — فـ «Al Hamza 20» تصير
  `al-hamza-20-91a6`. النتيجة أن عمارة باسم إنجليزي من كلمة واحدة تعمل
  صفحة شفافيتها، وواحدة من كلمتين لا تعمل، بـ 404 لا يشرح شيئاً.

  والإصلاح في الجذع لا في اللاحقة: اللاحقة تبقى أربع خانات ستّ عشرية
  كما يولّدها الخادم — توسيعها يقبل معرّفات لا ينتجها شيء، وقد نبّه
  اختبار موجود إلى ذلك حين قَبِل النمط الموسَّع `nada-zzzz`.
*/
const PUBLIC_ID_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*-[a-f0-9]{4}$/;

/** Route params are attacker-controlled. Validate shape before any lookup. */
export function isValidPublicId(value: unknown): value is string {
  return typeof value === 'string' && value.length <= LIMITS.publicId && PUBLIC_ID_PATTERN.test(value);
}

/** Positive, safe integers only — rejects "1e9", "-3", "1.5", "٣", NaN. */
export function parseId(value: unknown): number | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value);
  if (!/^\d{1,9}$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Blocks open redirects. Only same-origin absolute paths are accepted;
 * "//evil.com", "https://evil.com", and "javascript:..." are all rejected.
 */
export function safeInternalPath(value: unknown, fallback = '/'): string {
  if (typeof value !== 'string') return fallback;
  if (!value.startsWith('/')) return fallback;
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback;
  if (/[\u0000-\u001F]/.test(value)) return fallback;
  return value.slice(0, 512);
}

/* ---------------------------------------------------------- validators */

export const MONEY_PATTERN = /^\d{1,9}(\.\d{1,3})?$/;

export function isValidMoneyInput(value: string): boolean {
  return MONEY_PATTERN.test(value.trim());
}

/**
 * رقم صالح للدخول (#89).
 *
 * الأردني بصيغتيه (`07…` و`+962 7…`) كما كان، **وأي رقم دولي** بصيغة
 * E.164: ٨–١٥ خانة بلا صفر بادئ — لمالك الشقة الذي يعيش في الخليج
 * ولساكن بخطّ غير أردني. وما بدأ بـ`962` يبقى مشدوداً إلى الموبايل
 * الأردني، فلا يمرّ رقم أرضي أو ناقص بحجّة أنه دولي.
 */
export function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '').replace(/^00/, '');
  if (/^0/.test(digits)) return /^07[789]\d{7}$/.test(digits);
  if (/^962/.test(digits)) return /^9627[789]\d{7}$/.test(digits);
  if (/^7\d{8}$/.test(digits)) return true;           // 7XXXXXXXX أردني بلا صفر
  return /^[1-9]\d{7,14}$/.test(digits);
}

/** الاسم القديم — يبقى لمن يستورده. */
export const isValidJordanPhone = isValidPhone;

/** ISO date (YYYY-MM-DD) within a sane window — no year 0001, no 31 February. */
export function isValidDateInput(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return false;
  // Date silently rolls 2024-02-31 over to March; require an exact round-trip.
  if (date.toISOString().slice(0, 10) !== value) return false;
  const year = date.getUTCFullYear();
  return year >= 2015 && year <= new Date().getUTCFullYear() + 1;
}

export function isValidPeriod(year: unknown, month: unknown): boolean {
  return (
    typeof year === 'number' &&
    Number.isInteger(year) &&
    year >= 2015 &&
    year <= 2100 &&
    typeof month === 'number' &&
    Number.isInteger(month) &&
    month >= 1 &&
    month <= 12
  );
}
